/**
 * NotificationBell — header dropdown for the in-app notification feed: both
 * human-collaboration kinds (docs/COLLABORATION_CONTRACT.md §3:
 * `lead_assigned` + org-invite/share/role events) AND AI virtual-staff kinds
 * (`mas_persona_action`, `staff_decision_needed`, `staff_escalation`,
 * `staff_delivery` — an agent acted, needs arbitration, breached its SLA, or
 * finished a deliverable). Mounted additively in DashboardLayout's header
 * controls, next to the theme picker.
 *
 * Shows an unread badge, a Tout/Équipe IA/Collaboration segmented filter, a
 * recent list (high-priority AI-staff rows — decisions/escalations — stand
 * out with an accent border + a "Priorité" tag), per-row mark-read on click
 * with a kind-aware deep-link, "Tout marquer lu", and a gear button opening
 * the NotificationPrefs panel (which kinds are allowed to surface at all —
 * client-side, localStorage). Degrades to a quiet empty state instead of an
 * error when the feed endpoint isn't deployed yet (404/501/503 — see
 * lib/api/notifications.ts).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { kindInfo, notificationGroup, type AppNotification, type NotificationGroup } from '@/lib/api/notifications';
import { timeAgo } from '@/routes/crm/format';
import { NotificationPrefs } from './NotificationPrefs';
import { useNotifications } from './useNotifications';

/** Where a notification navigates on click, or null when there's no target.
 * AI-staff kinds route by `kind`/`type` first (their `entity_type` is almost
 * always the generic `agent_task`, which doesn't tell us WHICH AI-team
 * surface is relevant on its own — see FE-LANE 5 report for the route
 * rationale). Collaboration kinds keep the existing entity_type-first
 * routing: share notifications carry `entity_type` (the scope:
 * group|contact|all) + `entity_id` (the group/contact id) so we can open
 * exactly what was shared. */
function targetFor(n: AppNotification): string | null {
  const k = n.kind || n.type || '';
  if (k === 'staff_question') return '/staff-questions';
  if (k === 'staff_decision_needed') return '/approvals';
  if (k === 'staff_escalation') return '/ai-team/control';
  if (k === 'staff_delivery') return '/ai-team/deliverables';
  if (k === 'mas_persona_action') return '/ai-activity';

  const t = n.entity_type;
  const id = n.entity_id;
  if (t === 'group' && id) return `/crm/contacts?group_id=${encodeURIComponent(id)}`;
  if (t === 'contact' && id) return `/crm/contacts?contact_id=${encodeURIComponent(id)}`;
  if (t === 'all') return '/crm/contacts?shared=1';
  if (k.includes('share')) return '/crm/contacts?shared=1';
  if (k.includes('lead_assigned') || k.includes('assign'))
    return id ? `/crm/contacts?contact_id=${encodeURIComponent(id)}` : '/crm/contacts';
  if (k.includes('invite') || k.includes('role') || k.includes('member')) return '/settings/team';
  // Any future/unclassified AI-staff kind still lands somewhere useful.
  if (t === 'agent_task' || k.startsWith('mas_') || k.startsWith('staff_')) return '/ai-activity';
  return null;
}

const BellIcon = (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-[18px] w-[18px]"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a2 2 0 0 0 3.4 0" />
  </svg>
);

const GearIcon = (
  <svg
    aria-hidden="true"
    viewBox="0 0 20 20"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="10" cy="10" r="2.5" />
    <path d="M10 2.5v1.6M10 15.9v1.6M17.5 10h-1.6M4.1 10H2.5M15.13 4.87l-1.13 1.13M6 14l-1.13 1.13M15.13 15.13 14 14M6 6 4.87 4.87" />
  </svg>
);

type GroupFilter = 'all' | NotificationGroup;

const GROUP_FILTER_LABEL: Record<GroupFilter, string> = {
  all: 'Tout',
  ai_staff: 'Équipe IA',
  collaboration: 'Collaboration',
};

export function NotificationBell() {
  const { items, unreadCount, loading, error, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const filteredItems = useMemo(
    () => (groupFilter === 'all' ? items : items.filter((n) => notificationGroup(n) === groupFilter)),
    [items, groupFilter],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          unreadCount > 0
            ? `Notifications — ${unreadCount} non lue(s)`
            : 'Notifications'
        }
        title="Notifications"
        className="um-icon-btn relative h-9 w-9"
      >
        {BellIcon}
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white shadow-elevation-1"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Notifications"
          className="absolute right-0 top-full z-50 mt-1 w-80 max-w-[90vw] overflow-hidden rounded-token-lg border border-border bg-surface shadow-elevation-3"
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">
              Notifications
            </span>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => void markAllRead()}
                disabled={unreadCount === 0}
                className="text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
              >
                Tout marquer lu
              </button>
              <button
                type="button"
                onClick={() => setPrefsOpen(true)}
                aria-label="Préférences de notifications"
                title="Préférences de notifications"
                className="rounded-token-sm p-1 text-muted transition hover:bg-surface-2 hover:text-foreground"
              >
                {GearIcon}
              </button>
            </div>
          </div>

          <div
            role="group"
            aria-label="Filtrer les notifications"
            className="flex items-center gap-1 border-b border-border px-3 py-1.5"
          >
            {(['all', 'ai_staff', 'collaboration'] as GroupFilter[]).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroupFilter(g)}
                aria-pressed={groupFilter === g}
                className={`rounded-full px-2 py-0.5 text-[11px] font-medium transition ${
                  groupFilter === g
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted hover:bg-surface-2 hover:text-foreground'
                }`}
              >
                {GROUP_FILTER_LABEL[g]}
              </button>
            ))}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && items.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted">Chargement…</p>
            )}

            {!loading && error && items.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted">
                Notifications indisponibles pour le moment.
              </p>
            )}

            {!loading && !error && items.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted">Aucune notification.</p>
            )}

            {!loading && items.length > 0 && filteredItems.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted">
                Aucune notification dans ce filtre.
              </p>
            )}

            {filteredItems.length > 0 && (
              <ul className="divide-y divide-border">
                {filteredItems.map((n) => {
                  const info = kindInfo(n.kind ?? n.type);
                  const standout = !!info.highPriority && !n.read;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          if (!n.read) void markRead(n.id);
                          const to = targetFor(n);
                          setOpen(false);
                          if (to) navigate(to);
                        }}
                        className={`flex w-full items-start gap-2.5 border-l-2 px-3 py-2.5 text-left transition-colors hover:bg-surface-2 ${
                          standout
                            ? 'border-l-warning bg-warning/[0.08]'
                            : `border-l-transparent ${n.read ? '' : 'bg-primary/[0.04]'}`
                        }`}
                      >
                        <span aria-hidden className="mt-0.5 shrink-0 text-base leading-none">
                          {info.icon}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span
                              className={`truncate text-sm ${n.read ? 'text-foreground' : 'font-semibold text-foreground'}`}
                            >
                              {n.title}
                            </span>
                            {standout && (
                              <span className="shrink-0 rounded-full bg-warning/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning">
                                Priorité
                              </span>
                            )}
                            {!n.read && (
                              <span
                                aria-label="Non lue"
                                className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                              />
                            )}
                          </span>
                          {n.body && (
                            <span className="mt-0.5 block truncate text-xs text-muted">{n.body}</span>
                          )}
                          <span className="mt-0.5 block text-[11px] text-muted">
                            {timeAgo(n.created_at)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {prefsOpen && <NotificationPrefs onClose={() => setPrefsOpen(false)} />}
    </div>
  );
}
